import { useState } from "react";
import { useDispatch } from "react-redux";
import { ModalType } from "constants/enums";
import { openOrCloseModal, showModalHeader } from "shared/components/Modal/modalActionsSlice";
import { ColorRing } from "react-loader-spinner";
import { toast, ToastContainer, TypeOptions } from "react-toastify";
import { hooks } from "botframework-webchat";
import { storeFeedback } from "shared/functions/createFeedbackHistory";
import { SERVICENOW_CONFIG } from "constants/serviceNowConfig";
import './Feedback.css'

interface FeedbackProps {
  token: string
}

const Spinner = () => {
  return (
    <>
      <ColorRing
        visible={true}
        height="25"
        width="25"
        ariaLabel="color-ring-loading"
        wrapperStyle={{}}
        wrapperClass="color-ring-wrapper"
        colors={['#B0B0B0', '#B0B0B0', '#B0B0B0', '#B0B0B0', '#B0B0B0']}
      />
    </>
  )
}

export const Feedback = ({ token }: FeedbackProps) => {
  console.log('[Feedback] Componente inicializado con token:', token?.substring(0, 10) + '...');
  
  let dispatch = useDispatch();
  const [feedback, setFeedback] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [feedbackWasSent, setFeedbackWasSent] = useState<boolean>(false);

  const { usePostActivity } = hooks;
  const postActivity = usePostActivity();

  const onFeedbackChange = (value: string) => {
    console.log('[onFeedbackChange] Feedback actualizado:', value);
    setFeedback(value);
  }

  const dispatchToast = async (text: string, type: TypeOptions) => {
    console.log('[dispatchToast] Toast mostrado:', { text, type });
    toast(text, {
      theme: "colored",
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      position: "bottom-left",
      autoClose: 5000,
      type
  });
  }


  const sendFeedback = async () => {
    console.log('[sendFeedback] Iniciando envío de feedback...');
    setIsLoading(true)

    const activityId = localStorage.getItem('activityId')

    let userEmail = '';
    try {
      const jwtToken = localStorage.getItem('jwt') || '';
      const payload = JSON.parse(atob(jwtToken.split('.')[1]));
      userEmail = payload.user_id || '';
    } catch (e) {
      console.warn('[sendFeedback] No se pudo obtener el email del JWT');
    }
    console.log('[sendFeedback] Activity ID obtenido:', activityId);
    
    const conversationId = activityId?.split('|')[0]
    console.log('[sendFeedback] Conversation ID extraído:', conversationId);

    console.log('[sendFeedback] Enviando messageReaction...');
    postActivity({
      type: "messageReaction",
      activityID: activityId,
      reactionsAdded: [{ id: activityId, type: "dislike" }],
    });
    console.log('[sendFeedback] MessageReaction enviado');

    try {
      console.log('[sendFeedback] Realizando fetch a Direct Line API...');
      const response = await fetch(
      `https://directline.botframework.com/v3/directline/conversations/${conversationId}/activities`,
      {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          "type": "message",
          "value": {
              "action": "send_feedback_to_bot",
              "message": "Feedback from Dislike!",
              "feedback": feedback
          }
        })
      }
    );

      console.log('[sendFeedback] Respuesta Direct Line - Status:', response.status);

      if (!response.ok) {
        console.error('[sendFeedback] Error en Direct Line API - Status:', response.status);
        setIsLoading(false)
        await dispatchToast(`Direct Line token API call failed with status ${response.status}`, "error");
        throw new Error(
          `Direct Line token API call failed with status ${response.status}`
        );
      }

      const responseJson = await response.json();
      console.log('[sendFeedback] Respuesta Direct Line parseada:', responseJson);
      
      console.log('[sendFeedback] Almacenando feedback en historial...');
      storeFeedback(activityId || '',  responseJson.id);
      console.log('[sendFeedback] Feedback almacenado correctamente');

      // Obtener historial de la conversación para enriquecer el feedback
      let conversationContext = '';
      try {
        const activitiesResponse = await fetch(
          `https://directline.botframework.com/v3/directline/conversations/${conversationId}/activities`,
          {
            method: "GET",
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        if (activitiesResponse.ok) {
          const activitiesJson = await activitiesResponse.json();
          const activities = activitiesJson.activities || [];

          // Encontrar el mensaje del bot al que se hizo dislike
          const botActivityIndex = activities.findIndex((a: any) => a.id === activityId);
          if (botActivityIndex !== -1) {
            const botActivity = activities[botActivityIndex];
            const botText = botActivity.text || (botActivity.speak) || '';

            // Encontrar la pregunta del usuario que precede al mensaje del bot
            let userText = '';
            for (let i = botActivityIndex - 1; i >= 0; i--) {
              const a = activities[i];
              if ((a.from?.role === 'user' || a.from?.id?.toLowerCase().includes('user')) && a.text) {
                userText = a.text;
                break;
              }
            }

            if (userText || botText) {
              conversationContext = `\n\n--- Conversation Context ---\nUser: ${userText}\nBot: ${botText}`;
            }
          }
        }
      } catch (e) {
        console.warn('[sendFeedback] No se pudo obtener el historial de conversación:', e);
      }

      //PARA CREAR UN TICKET DE FORMA AUTOMÁTICA
      try {
        console.log('[sendFeedback] Intentando crear ticket en ServiceNow...');
        console.log('[sendFeedback] Backend API URL:', SERVICENOW_CONFIG.BACKEND_API);
        
        // Llamar al backend Flask en lugar de directamente a ServiceNow (evita CORS issues)
        const serviceNowResponse = await fetch(
          SERVICENOW_CONFIG.BACKEND_API,
          {
            method: "POST",
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              activityId: activityId,
              feedback: feedback + conversationContext,
              email: userEmail,
              region: "Cemex Central"
            })
          }
        );

        console.log('[sendFeedback] Respuesta ServiceNow - Status:', serviceNowResponse.status);

        if (!serviceNowResponse.ok) {
          console.error('[sendFeedback] Error en ServiceNow API - Status:', serviceNowResponse.status);
          await dispatchToast(`ServiceNow API call failed with status ${serviceNowResponse.status}`, "error");
        } else {
          console.log('[sendFeedback] Ticket de ServiceNow creado exitosamente');
        }
      } catch (error) {
        console.error('[sendFeedback] Error al enviar a ServiceNow:', error);
        await dispatchToast('Error sending feedback to ServiceNow', "error");
      }

      console.log('[sendFeedback] Finalizando proceso de envío...');
      setIsLoading(false)
      dispatch(showModalHeader(false))
      setFeedbackWasSent(true)
      console.log('[sendFeedback] Mostrando mensaje de éxito');
      
      setTimeout(() => {
        console.log('[sendFeedback] Cerrando modal de feedback después de 4 segundos');
        dispatch(openOrCloseModal({ isOpen: false, modalType: ModalType.Feedback }))
        dispatch(showModalHeader(true))
      }, 4000);
    } catch (error) {
      console.error('[sendFeedback] Error fatal:', error);
      setIsLoading(false);
    }
  }


  return (
    <>
      <div style={{ display: feedbackWasSent ? 'none' : 'block', width: '100%' }}>
        <div className="textarea-container">
          <textarea id="feedback" placeholder=" " required onChange={(e) => onFeedbackChange(e.target.value)}></textarea>
          <label htmlFor="feedback">Feedback</label>
          {+feedback.length === 0 && <span className="error-message">Feedback is required.</span> }
        </div>
        <div className="send-feedback--button__container">
          <button className={`send-feedback--button ${isLoading ? 'send-feedback--loading' : ''}`}
            disabled={+feedback.length === 0 || isLoading} 
            onClick={sendFeedback}> {isLoading && <Spinner />} Send feedback</button>
          <ToastContainer />
        </div>
      </div>

      <div style={{ display: feedbackWasSent ? 'block' : 'none' }} className="feedback-content-success-message">
        <img src="static/images/icons/Ok.png" className="ok-icon" />
        <h2 className="thank-you-title">Thank you!</h2>
        <p className="feedback-bot-message">Now I know this answer is INCORRECT! Thanks for your feedback!</p>
        <p className="feedback-bot-message">Your feedback has been registered for analysis as:</p>
        <p className="feedback-user-message">Your feedback is here: {feedback}</p>
      </div>
    </>
  );
};
